import { PProgress } from 'p-progress';
import type {
    IProgressPromise,
    ProgressNotifier,
    PromiseAsyncExecutor,
    PromiseExecutor,
} from '../interface/promise';
import promiseUtil from '../util/promise';

function _async<ValueType>(executor: PromiseAsyncExecutor<ValueType>): IProgressPromise<ValueType> {
    const wrapper = async (
        resolve: (value?: ValueType | PromiseLike<ValueType>) => void,
        reject: (reason?: unknown) => void,
        progress: ProgressNotifier,
    ) => {
        try {
            const result = await executor(progress);
            resolve(result);
        } catch (error: unknown) {
            reject(error);
        }
    };

    /* Incorrect in source version. See https://github.com/sindresorhus/p-progress/blob/d1e701f7f9179bc047ebc1a566e79c16fadccb3b/index.js#L167 */
    /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
    const promiser = new PProgress(wrapper);

    return promiser;
}

function _promise<ValueType>(executor: PromiseExecutor<ValueType>): IProgressPromise<ValueType> {
    /* Incorrect in source version. See https://github.com/sindresorhus/p-progress/blob/d1e701f7f9179bc047ebc1a566e79c16fadccb3b/index.js#L167 */
    /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
    const promiser = new PProgress(executor);
    return promiser;
}

function _retry<T>(
    factory: () => IProgressPromise<T>,
    delayBetweenRetries = 1000,
    retryCount = 0,
): IProgressPromise<T> {
    const promise = _async(async progressCallback => {
        if (retryCount < 0) {
            throw new Error('Retry count exceeded, cannot continue (<0)');
        }

        const promise = factory();
        promise.onProgress(progressCallback);

        let response = await promise.catch((error: Error) => error);

        if (response instanceof Error) {
            if (delayBetweenRetries > 0) {
                await promiseUtil.delay(delayBetweenRetries).catch(() => undefined);
            }

            const canRetry = retryCount > 1;

            if (canRetry) {
                response = await _retry<T>(factory, delayBetweenRetries, retryCount - 1);
            } else {
                throw response;
            }
        }

        return response;
    });

    return promise;
}

export default Object.freeze({
    async: _async,
    promise: _promise,
    retry: _retry,
});
